/**
 * Shared Date Utilities
 *
 * These utilities are used by both client and server for consistent
 * date/week calculations in reporting and exports.
 */

/**
 * Week start day type
 * 0 = Sunday, 1 = Monday
 */
export type WeekStartDay = 0 | 1;

/**
 * Gets the date key (YYYY-MM-DD format) in specified timezone.
 * This ensures date grouping is consistent across client and server.
 *
 * @param date - The date to format
 * @param timezone - IANA timezone identifier (e.g., "Europe/Berlin")
 * @returns Date string in YYYY-MM-DD format
 */
export function getDateKeyInTimezone(date: Date, timezone: string): string {
  const year = date.toLocaleString("en-US", { timeZone: timezone, year: "numeric" });
  const month = date.toLocaleString("en-US", { timeZone: timezone, month: "2-digit" });
  const day = date.toLocaleString("en-US", { timeZone: timezone, day: "2-digit" });
  return `${year}-${month}-${day}`;
}

/**
 * Gets the week key (YYYY-WNN format) in specified timezone.
 * Supports both ISO 8601 (Monday start) and US-style (Sunday start) week numbering.
 *
 * ISO 8601 rules (weekStartsOn = 1):
 * - Weeks start on Monday
 * - Week 1 is the week containing the first Thursday of the year
 * - The ISO week-numbering year may differ from calendar year at year boundaries
 *
 * US-style rules (weekStartsOn = 0):
 * - Weeks start on Sunday
 * - Week 1 is the week containing January 1st
 *
 * @param date - The date to calculate week key for
 * @param timezone - IANA timezone identifier (e.g., "Europe/Berlin")
 * @param weekStartsOn - 0 for Sunday, 1 for Monday (default: 1)
 * @returns Week key in YYYY-WNN format (e.g., "2024-W01")
 */
export function getWeekKey(date: Date, timezone: string, weekStartsOn: WeekStartDay = 1): string {
  // Get the date components in the target timezone
  const dateInTz = new Date(date.toLocaleString("en-US", { timeZone: timezone }));

  // Create a date object for calculations (using local methods, not UTC)
  const target = new Date(dateInTz.getFullYear(), dateInTz.getMonth(), dateInTz.getDate());

  if (weekStartsOn === 1) {
    // ISO 8601: Monday start
    // ISO week day: Monday = 1, Sunday = 7
    const dayOfWeek = target.getDay() || 7; // Convert Sunday from 0 to 7

    // Find the Thursday of this week (Thursday determines the ISO week's year)
    // Thursday is day 4, so we adjust: current + (4 - dayOfWeek)
    target.setDate(target.getDate() + 4 - dayOfWeek);

    // The ISO week-numbering year is the year of this Thursday
    const isoYear = target.getFullYear();

    // January 4th is always in week 1 (since week 1 contains the first Thursday)
    const jan4 = new Date(isoYear, 0, 4);

    // Find the Monday of week 1 (the Monday before or on Jan 4)
    const jan4DayOfWeek = jan4.getDay() || 7;
    const week1Monday = new Date(jan4);
    week1Monday.setDate(jan4.getDate() - (jan4DayOfWeek - 1));

    // Calculate the week number: days since week 1 Monday / 7 + 1
    const daysSinceWeek1 = Math.round((target.getTime() - week1Monday.getTime()) / (24 * 60 * 60 * 1000));
    const weekNumber = Math.floor(daysSinceWeek1 / 7) + 1;

    return `${isoYear}-W${weekNumber.toString().padStart(2, "0")}`;
  } else {
    // US-style: Sunday start
    // Week 1 contains January 1st
    const dayOfWeek = target.getDay(); // Sunday = 0

    // Find the Sunday of this week (the week start)
    const weekStart = new Date(target);
    weekStart.setDate(target.getDate() - dayOfWeek);

    // Determine the year for this week (use the year of the week's Saturday)
    const weekEnd = new Date(weekStart);
    weekEnd.setDate(weekStart.getDate() + 6);
    const year = weekEnd.getFullYear();

    // Find the first Sunday of the year (or Dec 31 if Jan 1 is Sunday)
    const jan1 = new Date(year, 0, 1);
    const jan1DayOfWeek = jan1.getDay();
    const firstSunday = new Date(year, 0, 1 - jan1DayOfWeek);

    // Calculate week number
    const daysSinceFirstSunday = Math.round((weekStart.getTime() - firstSunday.getTime()) / (24 * 60 * 60 * 1000));
    const weekNumber = Math.floor(daysSinceFirstSunday / 7) + 1;

    return `${year}-W${weekNumber.toString().padStart(2, "0")}`;
  }
}
